import React, { useState, useEffect, useRef, useMemo } from 'react';
import './index.scss';

// 图标导入
import DataSelectionIcon from '@op/assets/svg/data-selection-icon.svg';
import DefinitionOptimizationIcon from '@op/assets/svg/definition-optimization.svg';
import TopicTaggingIcon from '@op/assets/svg/topic-tagging-icon.svg';
import QualityCheckIcon from '@op/assets/svg/quality-check-icon.svg';
import AnalysisInfoIcon from '@op/assets/svg/analysis-info-icon.svg';
import AnalysisInfoIconError from '@op/assets/svg/analysis-info-icon-error.svg';
import ArrowBottomIcon from '@op/assets/svg/arrow-bottom.svg';
import StreamingIcon from '@op/assets/svg/icon-streaming.svg';
import TaskProgressIcon from '@op/assets/svg/icon-ai.svg';
import useLanguage from '@op/hooks/useLanguage';
import { CONTACT_EMAIL } from '@op/constants';
import { DBO_TM } from '@op/utils/format';

export enum EStepStatus {
  RUNNING = 'running',
  FAILED = 'failed',
  COMPLETED = 'completed',
}

export interface ITaskStep {
  key: string;
  title: string;
  subTitle: string;
  description: {
    zh: string;
    en: string;
  };
  status: EStepStatus;
  content?: string;
  /** 步骤完成时间（来自 callback_data.delivery_time） */
  deliveryTime?: string;
}

export interface IReportInfo {
  name: string;
  size: string;
  isGenerating: boolean;
}

export interface ITaskAnalysisProgressProps {
  expanded?: boolean;
  onExpandChange?: (expanded: boolean) => void;
  steps?: ITaskStep[];
  reportInfo?: IReportInfo;
  onPause?: () => void;
  className?: string;
  /** 是否启用动画效果（步骤逐条显示、打字机效果） */
  enableAnimation?: boolean;
  /** 每个步骤显示的延迟时间（毫秒） */
  stepDelay?: number;
  /** 打字机效果每个字符的延迟时间（毫秒） */
  typewriterDelay?: number;
  /** AI 转译内容 */
  aiContent?: string;
  /** 游戏封面图 URL（用于报告图标） */
  gameCover?: string;
  isStop: boolean;
  /** 任务状态 */
  taskStatus?: 'running' | 'completed' | 'failed';
  taskTime?: { start_time: string; end_time: string };
  successTagged?:boolean;
}

// 步骤图标映射
const STEP_ICON_MAP: Record<string, string> = {
  data_selection: DataSelectionIcon,
  definition_optimization: DefinitionOptimizationIcon,
  topic_tagging: TopicTaggingIcon,
  quality_check: QualityCheckIcon,
};

/**
 * 打字机效果 Hook
 * @param text 要显示的完整文本
 * @param enabled 是否启用打字机效果
 * @param delay 每个字符的延迟时间（毫秒）
 * @param startTyping 是否开始打字
 */
const useTypewriter = (
  text: string,
  enabled: boolean,
  delay = 30,
  startTyping = true,
): { displayText: string; isTyping: boolean } => {
  const [displayText, setDisplayText] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const indexRef = useRef(0);
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    // 清理之前的定时器
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }

    if (!enabled || !text || !startTyping) {
      setDisplayText(enabled ? '' : text);
      setIsTyping(false);
      indexRef.current = 0;
      return;
    }

    // 重置状态
    setDisplayText('');
    indexRef.current = 0;
    setIsTyping(true);

    const typeNextChar = (): void => {
      if (indexRef.current < text.length) {
        setDisplayText(text.slice(0, indexRef.current + 1));
        indexRef.current += 1;
        timerRef.current = setTimeout(typeNextChar, delay);
      } else {
        setIsTyping(false);
      }
    };

    timerRef.current = setTimeout(typeNextChar, delay);

    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, [text, enabled, delay, startTyping]);

  return { displayText, isTyping };
};

const StepIcon: React.FC<{ stepKey: string }> = ({ stepKey }) => {
  const iconSrc = STEP_ICON_MAP[stepKey];

  if (!iconSrc) {
    return null;
  }

  return (
    <div className="dbo-task-step__icon">
      <img src={iconSrc} alt="" />
    </div>
  );
};

/**
 * 步骤描述组件 - 带打字机效果
 */
const StepDescription: React.FC<{
  description: string;
  status: EStepStatus;
  enableAnimation: boolean;
  typewriterDelay: number;
}> = ({ description, status, enableAnimation, typewriterDelay }) => {
  const shouldType =
    status === EStepStatus.RUNNING || status === EStepStatus.COMPLETED;
  const { displayText } = useTypewriter(
    description,
    enableAnimation,
    typewriterDelay,
    shouldType,
  );

  const isEmptyText = (text: string) => {
    // 处理空字符串、null、undefined、全是空格的情况
    return !text || text.trim() === '';
  };

  return (
    // 先判断最终要显示的文本是否为空
    !isEmptyText(enableAnimation ? displayText : description) && (
      <span className="dbo-task-step__description">
        {enableAnimation ? displayText : description}
      </span>
    )
  );
};

// 报告图标组件 - 使用游戏封面图
const ReportIcon: React.FC<{ gameCover?: string }> = ({ gameCover }) => (
  <div className="dbo-task-report__icon">
    {gameCover ? (
      <img
        src={gameCover}
        alt="game cover"
        className="dbo-task-report__cover"
      />
    ) : (
      <div className="dbo-task-report__icon-placeholder" />
    )}
  </div>
);

const LoadingIcon: React.FC = () => (
  <div className="dbo-task-report__loading-icon">
    <img src={StreamingIcon} alt="loading" />
  </div>
);

/**
 * 加载态骨架屏组件
 * 参考 dba-copilot-answer-loading 实现
 */
const LoadingSkeleton: React.FC = () => (
  <div className="dbo-task-analysis-progress__loading">
    <div className="dbo-load-long" />
    <div className="dbo-load-mid" />
    <div className="dbo-load-short" />
  </div>
);

/**
 * 判断是否正在处理
 */
const isProcessing = (steps: ITaskStep[]): boolean =>
  steps.length > 0 && steps.length < 4;

/**
 * 判断是否步骤加载中
 */
const areStepsLoading = (steps: ITaskStep[]): boolean => steps.length === 0;

/**
 * 是否应该显示提示信息
 */
const shouldShowInfoTip = (
  isFailed: boolean,
  isStop: boolean,
  steps: ITaskStep[],
): boolean => {
  const loading = areStepsLoading(steps);
  const processing = isProcessing(steps);

  return !isFailed && !isStop && (loading || processing);
};

const TaskAnalysisProgress: React.FC<ITaskAnalysisProgressProps> = ({
  expanded = true,
  onExpandChange,
  steps = [],
  reportInfo,
  className = '',
  enableAnimation = true,
  typewriterDelay = 30,
  gameCover,
  taskStatus,
  isStop,
  taskTime,
  successTagged,
}) => {
  const { formatMessage, isEn } = useLanguage();

  const isFailed = taskStatus === 'failed'; // 是否任务失败
  const isComplete = taskStatus === 'completed';
  const showInfoTip = shouldShowInfoTip(isFailed, isStop, steps); // 是否显示提示信息（进行中提示）
  const showLoading = steps.length === 0 && !isStop && !isFailed; // 是否显示加载骨架屏

  // 是否显示报告区域（只有在所有步骤完成且有报告信息时显示）
  const showReportSection = isComplete && reportInfo;

  /**
   * 获取失败提示文本
   */
  const getFailedMessageText = (
    isStopped: boolean,
    isFailed: boolean,
    stepsCount: number,
  ): string => {
    if (isStopped) {
      return formatMessage({ id: 'User manually stopped the task.' });
    }
    if (isFailed) {
      if (stepsCount === 0) {
        return formatMessage({ id: 'Task delivery failed.' });
      }
      return formatMessage({
        id: 'Current step failed. For any questions, please contact {email}.',
      }, { email: CONTACT_EMAIL });
    }
    return '';
  };

  /**
   * 获取提示信息元素
   */
  const getInfoTipElement = (
    showInfoTipValue: boolean,
    stoppedValue: boolean,
    failedValue: boolean,
    stepsCountValue: number,
  ): React.ReactNode => {
    if (showInfoTipValue) {
      return (
        <div className="dbo-task-analysis-progress__tip">
          <img
            src={AnalysisInfoIcon}
            alt="info"
            className="dbo-task-analysis-progress__tip-icon"
          />
          <span className="dbo-task-analysis-progress__tip-text">
            {formatMessage({
              id: 'The analysis is currently being generated. If you feel the task planning is inaccurate, you can pause at any time.',
            })}
          </span>
        </div>
      );
    }
    if (steps.length !== 0) {
      return null;
    }

    if (stoppedValue || failedValue) {
      return (
        <div className="dbo-task-step--error">
          <img
            src={AnalysisInfoIconError}
            alt="error"
            className="dbo-task-analysis-progress__tip-icon"
          />
          <span className="dbo-task-analysis-progress__tip-text">
            {getFailedMessageText(stoppedValue, failedValue, stepsCountValue)}
          </span>
        </div>
      );
    }
    return null;
  };

  /**
   * 获取步骤内容元素
   */
  const getStepsContentElement = (
    showLoadingValue: boolean,
  ): React.ReactNode => {
    if (showLoadingValue) {
      return <LoadingSkeleton />;
    }

    if (steps.length === 0) {
      return null;
    }

    return (
      <div className="dbo-task-analysis-progress__steps">
        {steps.map((step, index) => (
          <React.Fragment key={step.key}>
            <div
              className={`dbo-task-step dbo-task-step--${step.status} ${
                enableAnimation ? 'dbo-task-step--animate-in' : ''
              }`}
              style={
                enableAnimation
                  ? { animationDelay: `${index * 0.1}s` }
                  : undefined
              }
            >
              <StepIcon stepKey={step.key} />
              <div className="dbo-task-step__content">
                <span className="dbo-task-step__title">
                  {formatMessage({ id: step.title })}
                </span>
                <span className="dbo-task-step__sub_title">
                  {formatMessage({ id: step.subTitle })}
                </span>
              </div>
            </div>
            <StepDescription
              description={step.description[isEn ? 'en' : 'zh']}
              status={step.status}
              enableAnimation={enableAnimation}
              typewriterDelay={typewriterDelay}
            />
          </React.Fragment>
        ))}
        {(isStop || isFailed) && (
          <div className="dbo-task-step--error ">
            <img
              src={AnalysisInfoIconError}
              alt="error"
              className="dbo-task-step__icon"
            />
            {getFailedMessageText(isStop, isFailed, steps.length)}
          </div>
        )}
      </div>
    );
  };

  const timePrefix = useMemo(() => {
    if (!taskTime) return '';
    return (
      <div className="dbo-task-analysis-progress__title_time">
        {`${DBO_TM.timeTitle({
          ...taskTime,
          date_type: 'hourly',
          link_text: ' ~ ',
        })}`}
      </div>
    );
  }, [taskTime]);

  return (
    <div className={`dbo-task-analysis-progress ${className}`}>
      <div
        className="dbo-task-analysis-progress__header"
        onClick={() => onExpandChange?.(!expanded)}
      >
        <div className="dbo-task-analysis-progress__title-wrap">
          <img
            src={TaskProgressIcon}
            alt="task progress"
            className="dbo-task-analysis-progress__title-icon"
          />
          <span className="dbo-task-analysis-progress__title">
            {formatMessage({
              id: successTagged ? 'Latest Analysis Task Progress' : 'Analysis Task Progress',
            })}
            {timePrefix}
          </span>
          <img
            src={ArrowBottomIcon}
            alt="arrow"
            className={`dbo-task-analysis-progress__arrow ${
              expanded ? 'dbo-task-analysis-progress__arrow--up' : ''
            }`}
          />
        </div>
      </div>

      {expanded && (
        <div className="dbo-task-analysis-progress__content">
          <div className="dbo-task-analysis-progress__timeline" />

          <div className="dbo-task-analysis-progress__body">
            {getInfoTipElement(showInfoTip, isStop, isFailed, steps.length)}
            {getStepsContentElement(showLoading)}

            {showReportSection && (
              <div
                className={`dbo-task-report ${
                  reportInfo.isGenerating ? 'dbo-task-report--generating' : ''
                }`}
              >
                <ReportIcon gameCover={gameCover} />
                <div className="dbo-task-report__info">
                  <div className="dbo-task-report__name">{reportInfo.name}</div>
                  <div className="dbo-task-report__size">{reportInfo.size}</div>
                </div>
                {reportInfo.isGenerating && (
                  <>
                    <div className="dbo-task-report__shimmer" />
                    <div className="dbo-task-analysis-progress__generating-tip">
                      <LoadingIcon />
                      <span className="dbo-task-analysis-progress__generating-text">
                        {formatMessage({ id: 'report_generating' })}
                      </span>
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default TaskAnalysisProgress;
